/*
 * Copyright (c) Nexign, JSC, 2023-2024.
 */
/**
 * Product Offerings Sagas for select MegaPowers step
 *
 * @author Ilya Baranchikov
 * @version 1.27.1
 * @since 1.27.0
 */

import { Fragment, ReactNode, useCallback, useMemo, useState } from 'react';

import { Button, Modal, Typography } from '@nexign/scc-ui-kit';
import { useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { LinkBlock } from '@/main/components/common';
import { ImgStyled } from '@/main/components/common/Block/Img';
import { IWizardOrder } from '@/main/modules/wizard/wizardTypes';
import { getPageOrderIdUrl } from '@/main/pages/PageOrderId';
import { IRoutePath, RoutePath } from '@/main/routes/constants/routesConstants';
import { stringCapitalizeFirstLetter, stringFormatParams } from '@/main/utils/stringFormat';
import { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { makeSelectGuardComponents } from '@modules/role';

import {
    ModalMultipleOrdersButtonsContainer,
    ModalMultipleOrdersStyled,
    ModalMultipleOrdersTextContainer,
} from './styled/ModalMultipleOrdersStyled';

export type TModalMultipleOrdersProps = {
    isOpen: boolean;
    orderOrOrders: IWizardOrder['orderId'] | IWizardOrder[];
    onClose?: () => void;
    successButton?: string;
    onSuccessButtonClick?: (...args: any[]) => void;
    extraButton?: string;
    onExtraButtonClick?: () => void;
    onLinkClickUrl?: IRoutePath[keyof IRoutePath];
    ordersStatusLinkTranslation?: string;
    ordersStatusTextTranslation?: string;
    orderTypeTranlation?: string;
};

export const ModalMultipleOrders = ({
    isOpen,
    orderOrOrders,
    successButton,
    onSuccessButtonClick,
    extraButton,
    onExtraButtonClick,
    onLinkClickUrl,
    ordersStatusLinkTranslation,
    ordersStatusTextTranslation,
    onClose,
    orderTypeTranlation,
}: TModalMultipleOrdersProps) => {
    const [isModalOpen, setIsModalOpen] = useState(isOpen);
    const { orderCreationModal: t } = useSelector(makeSelectTranslationCommon());
    const componentsFeature = useSelector(makeSelectGuardComponents());

    const handleOnClose = () => {
        onClose && onClose();
        setIsModalOpen(false);
    };

    const handleOnSuccessClick = () => {
        onSuccessButtonClick && onSuccessButtonClick();
        setIsModalOpen(false);
    };

    const handleOnExtraButtonClick = () => {
        onExtraButtonClick && onExtraButtonClick();
        setIsModalOpen(false);
    };

    const handleLinkClick = () => {
        onClose && onClose();

        if (onLinkClickUrl) {
            appHistoryPush(onLinkClickUrl);
        } else {
            appHistoryPush(RoutePath.orders);
        }
    };

    const commaOrAndSeparatorByIndex = useCallback(
        (index: number, arrayLength: number): string => {
            if (arrayLength === 0) return '';
            if (arrayLength === 1) return '';
            switch (index) {
                case 0:
                    if (arrayLength === 2) return t.success.orderSeparator.and;
                    return t.success.orderSeparator.comma;
                case arrayLength - 1:
                    return '';
                case arrayLength - 2:
                    return t.success.orderSeparator.and;
                default:
                    return t.success.orderSeparator.comma;
            }
        },
        [t.success.orderSeparator.and, t.success.orderSeparator.comma],
    );

    const mapOrderOrOrdersToModalText = useMemo((): ReactNode => {
        if (Array.isArray(orderOrOrders)) {
            return (
                <span>
                    {orderOrOrders.map(({ orderId, type }, index) => (
                        <Fragment key={orderId}>
                            {index === 0 ? stringCapitalizeFirstLetter(t.success.order) : t.success.order}
                            <LinkBlock url={getPageOrderIdUrl(componentsFeature, orderId)}>
                                {stringFormatParams(t.success.orderNumber, { number: orderId })}
                            </LinkBlock>
                            {t.success.orderType[type] || ''}
                            {commaOrAndSeparatorByIndex(index, orderOrOrders.length)}
                        </Fragment>
                    ))}

                    {t.success.orderCreated[orderOrOrders.length > 1 ? 'few' : 'one']}
                </span>
            );
        } else if (typeof orderOrOrders === 'number' || typeof orderOrOrders === 'string') {
            return (
                <span>
                    {stringCapitalizeFirstLetter(t.success.order)}
                    <LinkBlock url={getPageOrderIdUrl(componentsFeature, orderOrOrders)}>
                        {stringFormatParams(t.success.orderNumber, { number: orderOrOrders })}
                    </LinkBlock>
                    {orderTypeTranlation || ''}
                    {t.success.orderCreated['one']}
                </span>
            );
        }
    }, [
        commaOrAndSeparatorByIndex,
        componentsFeature,
        orderOrOrders,
        orderTypeTranlation,
        t.success.order,
        t.success.orderCreated,
        t.success.orderNumber,
        t.success.orderType,
    ]);

    const mapOrderOrOrdersToTitleText = (): string => {
        if (Array.isArray(orderOrOrders)) {
            return orderOrOrders.length > 1 ? t.success.title.few : t.success.title.one;
        } else {
            return t.success.title.one;
        }
    };

    return (
        <Modal open={isModalOpen} title={' '} onClose={handleOnClose}>
            <ModalMultipleOrdersStyled>
                <ImgStyled height="144px" src={icons.confetti} width="144px" />
                <ModalMultipleOrdersTextContainer>
                    <Typography component={'span'} variant="h3">
                        {mapOrderOrOrdersToTitleText()}
                    </Typography>
                    <Typography component={'span'}>
                        {mapOrderOrOrdersToModalText}
                        {ordersStatusTextTranslation || t.success.ordersStatusText}
                        <LinkBlock url={onLinkClickUrl || RoutePath.orders} onClick={handleLinkClick}>
                            {ordersStatusLinkTranslation || t.success.ordersStatusLink}
                        </LinkBlock>{' '}
                    </Typography>
                </ModalMultipleOrdersTextContainer>
                <ModalMultipleOrdersButtonsContainer>
                    <Button onClick={handleOnSuccessClick}>
                        {successButton ? successButton : t.success.buttons.goToMain}
                    </Button>
                    {extraButton && (
                        <Button color="info" size="medium" variant="text" onClick={handleOnExtraButtonClick}>
                            {extraButton}
                        </Button>
                    )}
                </ModalMultipleOrdersButtonsContainer>
            </ModalMultipleOrdersStyled>
        </Modal>
    );
};
